import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import styles from './AnchorMenu.module.scss';

import { useAuth } from '@/app/providers/AuthContext';
import { orderStore } from '@/entities/order/store/orderStore';
import { STORAGE_KEYS } from '@/shared/constants/storageKeys';
import { OrdersWidget } from '@/widgets/orders/ui/Orders';

interface AnchorSection {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface AnchorMenuProps {
  sections: AnchorSection[];
}

export const AnchorMenu = ({ sections }: AnchorMenuProps) => {
  const firstId = sections[0]?.id;

  const [activeId, setActiveId] = useState<string>(firstId);
  const activeIdRef = useRef(activeId);

  const { isLoggedIn } = useAuth();

  const menuRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const prevActiveIdRef = useRef<string | null>(null);

  const programmaticLockRef = useRef(false);
  const unlockTimerRef = useRef<number | null>(null);
  const targetIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  useEffect(() => {
    if (isLoggedIn) {
      const savedOrderId = localStorage.getItem(STORAGE_KEYS.lastOrderId);
      if (savedOrderId) {
        orderStore.fetch(Number(savedOrderId));
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!sections.length) return;
    if (!activeIdRef.current) {
      setActiveId(sections[0].id);
      return;
    }
    const exists = sections.some((s) => s.id === activeIdRef.current);
    if (!exists) setActiveId(sections[0].id);
  }, [sections]);

  const clearUnlockTimer = useCallback(() => {
    if (unlockTimerRef.current) {
      window.clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = null;
    }
  }, []);

  const unlockProgrammatic = useCallback(() => {
    programmaticLockRef.current = false;
    targetIdRef.current = null;
    clearUnlockTimer();
  }, [clearUnlockTimer]);

  const handleClick = useCallback(
    (id: string) => {
      if (!id) return;

      programmaticLockRef.current = true;
      targetIdRef.current = id;
      clearUnlockTimer();

      setActiveId(id);

      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      unlockTimerRef.current = window.setTimeout(() => {
        unlockProgrammatic();
      }, 6000);
    },
    [clearUnlockTimer, unlockProgrammatic]
  );

  useEffect(() => {
    if (!sections.length) return;

    let ticking = false;
    const OFFSET_TOP = 150;

    const getTop = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return Number.POSITIVE_INFINITY;
      return el.getBoundingClientRect().top;
    };

    const updateActive = () => {
      if (programmaticLockRef.current) {
        const targetId = targetIdRef.current;
        if (targetId) {
          const top = getTop(targetId);
          if (Number.isFinite(top) && Math.abs(top - OFFSET_TOP) <= 8) {
            unlockProgrammatic();
          }
        }
        return;
      }

      const doc = document.documentElement;
      const atBottom = doc.scrollTop + doc.clientHeight >= doc.scrollHeight - 2;

      if (atBottom) {
        const lastId = sections[sections.length - 1].id;
        if (lastId && lastId !== activeIdRef.current) setActiveId(lastId);
        return;
      }

      let current: string | null = null;

      for (let i = 0; i < sections.length; i += 1) {
        const s = sections[i];
        const top = getTop(s.id);
        if (top <= OFFSET_TOP) current = s.id;
        else break;
      }

      const nextId = current ?? sections[0].id;
      if (nextId && nextId !== activeIdRef.current) setActiveId(nextId);
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActive();
          ticking = false;
        });
        ticking = true;
      }
    };

    updateActive();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      unlockProgrammatic();
    };
  }, [sections, unlockProgrammatic]);

  useEffect(() => {
    const menu = menuRef.current;
    const activeBtn = tabRefs.current[activeId || ''];
    if (!menu || !activeBtn || !sections.length) return;

    const prevActiveId = prevActiveIdRef.current;
    prevActiveIdRef.current = activeId;

    const currIndex = sections.findIndex((s) => s.id === activeId);
    const prevIndex = prevActiveId
      ? sections.findIndex((s) => s.id === prevActiveId)
      : -1;

    const direction: 'left' | 'right' =
      prevIndex === -1 || currIndex >= prevIndex ? 'right' : 'left';

    const menuRect = menu.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();

    const isFullyVisible =
      btnRect.left >= menuRect.left && btnRect.right <= menuRect.right;

    if (isFullyVisible) return;

    const targetLeft = activeBtn.offsetLeft;
    const targetRight = targetLeft + activeBtn.offsetWidth;
    const containerWidth = menu.clientWidth;

    let nextScrollLeft =
      direction === 'left'
        ? targetLeft - 10
        : targetRight - containerWidth + 10;

    const max = menu.scrollWidth - containerWidth;
    if (nextScrollLeft < 0) nextScrollLeft = 0;
    if (nextScrollLeft > max) nextScrollLeft = max;

    menu.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth' as ScrollBehavior,
    });
  }, [activeId, sections]);

  if (!sections.length) return null;

  return (
    <div className={styles.container}>
      <div ref={menuRef} className={styles.menu}>
        {sections.map(({ id, label }) => (
          <button
            key={id}
            ref={(el) => {
              tabRefs.current[id] = el;
            }}
            className={`${styles.tab} ${activeId === id ? styles.active : ''}`}
            onClick={() => handleClick(id)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.bg}>
        <OrdersWidget />

        <div className={styles.sections}>
          {sections.map(({ id, label, content }) => (
            <InView key={id} threshold={0}>
              {({ ref }) => (
                <section ref={ref} id={id} className={styles.section}>
                  <h2 className={styles.sectionTitle}>{label}</h2>
                  {content}
                </section>
              )}
            </InView>
          ))}
        </div>
      </div>
    </div>
  );
};
